<template>
  <tr
    class="verified-anomaly-row data-row is-openable"
    :class="{ 'is-active': active, 'is-selected': selected }"
    tabindex="0"
    :data-id="finding.id"
    :aria-label="`Verified anomaly ${identity}, ${scanName || 'Scan unavailable'}`"
    @click="$emit('open', finding)"
    @keydown.enter.self.prevent="$emit('open', finding)"
    @keydown.space.self.prevent="$emit('open', finding)">
    <td class="col-select">
      <input
        type="checkbox"
        :checked="selected"
        :disabled="busy"
        :aria-label="`Select anomaly ${identity}`"
        @click.stop="$emit('select', { id: finding.id, checked: $event.target.checked })" />
    </td>
    <td class="col-id">
      <div class="identity-cell">
        <v-tooltip v-if="thumbnail && !imageFailed" right :z-index="999" open-delay="250" content-class="verified-anomaly-thumb-tooltip">
          <template v-slot:activator="{ on, attrs }">
            <img
              :src="thumbnail"
              :alt="`Preview anomaly ${identity}`"
              tabindex="0"
              class="anomaly-thumb"
              v-bind="attrs"
              v-on="on"
              @error="imageFailed = true" />
          </template>
          <span class="anomaly-thumb-preview"><img :src="thumbnail" alt="" /></span>
        </v-tooltip><span
          v-else
          class="anomaly-thumb anomaly-thumb--missing"
          title="No anomaly thumbnail"
          ><v-icon :size="18">mdi-image-off-outline</v-icon></span
        ><span class="mono-id" :title="identity">{{ identity }}</span>
        <v-tooltip v-if="hasPoint" top open-delay="250">
          <template v-slot:activator="{ on, attrs }">
            <span
              class="finding-position-status"
              role="img"
              tabindex="0"
              aria-label="3D position available"
              v-bind="attrs"
              v-on="on">
              <v-icon :size="16" color="primary" aria-hidden="true">
                mdi-map-marker-check
              </v-icon>
            </span>
          </template>
          <span>3D position available</span>
        </v-tooltip>
      </div>
    </td>
    <td :title="componentLabel">
      <span v-if="linkedComponent" class="verified-component-wrap">
        <button
          type="button"
          class="verified-component"
          :disabled="busy"
          :aria-label="`Component of anomaly ${identity}: ${linkedComponent.name}. Change`"
          @click.stop="$emit('link', finding)">
          <v-icon :size="15" class="verified-component__icon">{{ componentIcon }}</v-icon>
          <span class="verified-component__name">{{ linkedComponent.name }}</span>
          <v-icon :size="14" class="verified-component__caret" aria-hidden="true">mdi-menu-down</v-icon>
        </button>
        <button
          type="button"
          class="verified-component-unlink"
          :disabled="busy"
          :aria-label="`Remove the component link of anomaly ${identity}`"
          title="Remove link"
          @click.stop="$emit('unlink', finding)">
          <v-icon :size="13">mdi-close</v-icon>
        </button>
      </span>
      <button
        v-else
        type="button"
        class="verified-component verified-component--none"
        :disabled="busy"
        :aria-label="`Link anomaly ${identity} to a component`"
        @click.stop="$emit('link', finding)">
        <v-icon :size="15" class="verified-component__icon">mdi-link-variant-plus</v-icon>
        <span class="verified-component__name">Not linked</span>
      </button>
    </td>
    <td class="col-location" :title="locationLabel">
      <div class="cell-main">{{ scanName || 'Scan unavailable' }}</div>
      <div class="cell-sub location-sub">{{ locationLabel }}</div>
    </td>
    <td :title="defectLabel">
      <div class="cell-main">{{ finding.defectCategorization || finding.type || '—' }}</div>
      <div class="defect-chips">
        <v-chip
          v-if="categoryChip"
          label
          x-small
          :class="['defect-chip', `defect-chip--cat-${categoryChip.code}`]"
          :title="categoryChip.tooltip">
          {{ categoryChip.text }}
        </v-chip>
        <v-chip
          v-if="riChip"
          label
          x-small
          :class="['defect-chip', 'defect-chip--ri', `defect-chip--ri-${riChip.code}`]"
          :title="riChip.tooltip">
          {{ riChip.text }}
        </v-chip>
        <v-chip
          v-if="otherCategoryChip"
          label
          x-small
          class="defect-chip defect-chip--neutral"
          :title="otherCategoryChip">
          {{ otherCategoryChip }}
        </v-chip>
        <span v-if="!categoryChip && !riChip && !otherCategoryChip" class="cell-sub cell-empty">—</span>
      </div>
    </td>
    <td>
      <span :class="['priority-pill', priorityClass]">{{ priorityLabel }}</span>
    </td>
    <td :title="plans.join(', ') || 'No plan assignments'">
      <span :class="{ 'cell-muted': !plans.length }">{{
        plans.length ? plans.join(', ') : 'No plans'
      }}</span>
    </td>
    <td class="actions col-actions" @click.stop @keydown.stop>
      <v-menu offset-y left>
        <template v-slot:activator="{ on, attrs }"
          ><v-btn
            icon
            small
            class="row-more"
            :disabled="busy"
            :aria-label="`Actions for anomaly ${identity}`"
            v-bind="attrs"
            v-on="on"
            ><v-icon :size="19">mdi-dots-vertical</v-icon></v-btn
          ></template
        >
        <v-list dense>
          <v-list-item @click.stop="$emit('report', finding)"
            ><v-list-item-icon><v-icon small>mdi-file-document-outline</v-icon></v-list-item-icon
            ><v-list-item-title>Finding report</v-list-item-title></v-list-item
          >
          <v-list-item
            :disabled="!canIsolate"
            :title="
              canIsolate
                ? 'Show available spatial context'
                : 'No 3D point or model geometry available'
            "
            @click.stop="$emit('isolate', finding)"
            ><v-list-item-icon><v-icon small>mdi-cube-outline</v-icon></v-list-item-icon
            ><v-list-item-content
              ><v-list-item-title>Show in 3D</v-list-item-title
              ><v-list-item-subtitle v-if="!canIsolate"
                >No 3D point or model geometry</v-list-item-subtitle
              ></v-list-item-content
            ></v-list-item
          >
          <v-list-item
            :disabled="!hasPoint"
            :title="hasPoint ? 'Open Corrosion Analyser' : 'A valid 3D point is required'"
            @click.stop="$emit('corrosion', finding)"
            ><v-list-item-icon><v-icon small>mdi-chart-scatter-plot</v-icon></v-list-item-icon
            ><v-list-item-content
              ><v-list-item-title>Corrosion Analyser</v-list-item-title
              ><v-list-item-subtitle v-if="!hasPoint"
                >A valid 3D point is required</v-list-item-subtitle
              ></v-list-item-content
            ></v-list-item
          >
          <v-divider />
          <v-list-item @click.stop="$emit('delete', finding)"
            ><v-list-item-icon
              ><v-icon small color="error">mdi-delete-outline</v-icon></v-list-item-icon
            ><v-list-item-title class="error--text">Delete anomaly…</v-list-item-title></v-list-item
          >
        </v-list>
      </v-menu>
    </td>
  </tr>
</template>

<script>
  import { componentOfFinding, componentTypeIcon, componentTypeOf } from '../../utils/findingComponents';
  import { hasSpatialPoint } from '../../utils/verifiedAnomalyViewer';
  import { stripCat, stripRi } from '../../utils/findingsMetrics';
  import { coatingBreakdown, pittingCategory } from '../../config/enums';
  export default {
    name: 'VerifiedAnomalyRow',
    props: {
      finding: { type: Object, required: true },
      scanName: { type: String, default: '' },
      active: { type: Boolean, default: false },
      selected: { type: Boolean, default: false },
      busy: { type: Boolean, default: false },
    },
    data() {
      return { imageFailed: false };
    },
    computed: {
      identity() {
        return this.finding.friendlyId || this.finding.id;
      },
      thumbnail() {
        return this.finding.tiledImageUriPath || this.finding.thumbnailUrl || '';
      },
      componentLabel() {
        return this.finding.masterTagId
          ? this.finding.masterTagName || 'Linked component unavailable'
          : 'Not linked';
      },
      linkedComponent() {
        return componentOfFinding(this.finding);
      },
      componentIcon() {
        return componentTypeIcon(this.linkedComponent && this.linkedComponent.type);
      },
      locationLabel() {
        return (
          [this.finding.locationDescription, this.finding.component].filter(Boolean).join(' · ') ||
          'Location not recorded'
        );
      },
      defectLabel() {
        return (
          [
            this.finding.defectCategorization,
            this.finding.type,
            this.finding.category,
            this.finding.pittingCategory,
            this.finding.coatingBreakdown,
          ]
            .filter(Boolean)
            .join(' · ') || 'Not recorded'
        );
      },
      categoryChip() {
        const raw = this.finding.pittingCategory || this.finding.category;
        if (!raw) return null;
        const letter = stripCat(raw).toUpperCase();
        const knownCat = pittingCategory.find(
          (c) =>
            c.id.toLowerCase() === `cat${letter.toLowerCase()}` ||
            c.name.toLowerCase() === String(raw).toLowerCase(),
        );
        if (knownCat) {
          const code = knownCat.id.replace(/^cat/i, '').toLowerCase();
          return {
            text: knownCat.name,
            code,
            severity: knownCat.severityLevel || 'low',
            tooltip: `Corrosion category: ${knownCat.name} · ${knownCat.tooltip || knownCat.description || ''}`,
          };
        }
        if (['A', 'B', 'C', 'D'].includes(letter)) {
          const code = letter.toLowerCase();
          const severityMap = { a: 'low', b: 'moderate', c: 'high', d: 'critical' };
          return {
            text: `Cat ${letter}`,
            code,
            severity: severityMap[code] || 'low',
            tooltip: `Corrosion category: Cat ${letter}`,
          };
        }
        return {
          text: String(raw),
          code: 'other',
          severity: 'low',
          tooltip: `Category: ${raw}`,
        };
      },
      riChip() {
        const raw = this.finding.coatingBreakdown;
        if (!raw) return null;
        const num = stripRi(raw);
        const knownRi = coatingBreakdown.find(
          (c) =>
            c.id.toLowerCase() === `ri${num}` ||
            c.name.toLowerCase() === String(raw).toLowerCase(),
        );
        if (knownRi) {
          const code = knownRi.id.replace(/^ri/i, '');
          const severity = Number(code) >= 4 ? 'critical' : Number(code) >= 2 ? 'moderate' : 'low';
          return {
            text: knownRi.name,
            code,
            severity,
            tooltip: `Coating breakdown: ${knownRi.name} · ${knownRi.tooltip || ''}`,
          };
        }
        if (/^\d+$/.test(num)) {
          const severity = Number(num) >= 4 ? 'critical' : Number(num) >= 2 ? 'moderate' : 'low';
          return {
            text: `Ri${num}`,
            code: num,
            severity,
            tooltip: `Coating breakdown: Ri${num}`,
          };
        }
        return {
          text: String(raw),
          code: 'custom',
          severity: 'low',
          tooltip: `Coating breakdown: ${raw}`,
        };
      },
      otherCategoryChip() {
        const cat = this.finding.category;
        if (!cat) return null;
        if (
          this.categoryChip &&
          (cat === this.finding.pittingCategory ||
            stripCat(cat).toUpperCase() === stripCat(this.categoryChip.text).toUpperCase())
        ) {
          return null;
        }
        return String(cat);
      },
      priorityClass() {
        if (this.finding.severity == null) return 'priority-pill--none';
        const str = String(this.finding.severity).toLowerCase();
        if (str === '0' || str === 'low') return 'priority-pill--level-0';
        if (str === '1') return 'priority-pill--level-1';
        if (str === '2' || str === 'medium') return 'priority-pill--level-2';
        if (str === '3' || str === 'high') return 'priority-pill--level-3';
        if (str === '4' || str === 'critical') return 'priority-pill--level-4';
        return `priority-pill--level-${str}`;
      },
      priorityLabel() {
        if (this.finding.severity == null) return 'Not set';
        if (/^\d+$/.test(String(this.finding.severity))) {
          return `Level ${this.finding.severity}`;
        }
        return String(this.finding.severity);
      },
      plans() {
        return (this.finding.taskBoards || []).map((plan) =>
          typeof plan === 'string' ? plan : plan.name || plan.title || 'Plan',
        );
      },
      canIsolate() {
        return !!(
          this.hasPoint ||
          (this.finding.masterTagId && componentTypeOf(this.finding.masterTagType)) ||
          (this.finding.dbIds || []).length ||
          ((this.finding.inspection || {}).children || []).some(
            (child) => child && child.dbId != null,
          )
        );
      },
      hasPoint() {
        return hasSpatialPoint(this.finding.point3d);
      },
    },
    watch: {
      thumbnail() {
        this.imageFailed = false;
      },
    },
  };
</script>
